import {inject, Injectable} from '@angular/core';
import {ModalsService} from "../../../services/modals.service";
import {ActionHandlerContext, ActionHandlers} from "../types/action-handlers.types";
import {RecordTableAction} from "../../../types/record-table-action.types";
import {HandleDownloadExcelParams} from "../types/record-table-download.types";
import {DownloadExcelRequestParams} from "../../../types/records-table.types";
import {RecordTableApiService} from "../../../services-api/record-table-api.service";
import {UtilityService} from "../../../services/utility.service";
import {ErrorService} from "../../../services/error.service";

@Injectable({
  providedIn: 'root'
})
export class RecordTableActionsService {
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private modalsService: ModalsService = inject(ModalsService);
  private utilityService: UtilityService = inject(UtilityService);
  private errorService: ErrorService = inject(ErrorService);

  private actionHandlers: Partial<ActionHandlers> = {
    DOWNLOAD_EXCEL: async (context) => {
      this.handleDownloadExcel({
        resourceParams: context.resourceParams,
        queryParams: context.queryParams,
        excelType: 'DOWNLOAD_EXCEL',
      });
    },
    DOWNLOAD_OLD_FORMAT_EXCEL: async (context) => {
      this.handleDownloadExcel({
        resourceParams: context.resourceParams,
        queryParams: context.queryParams,
        excelType: 'DOWNLOAD_OLD_FORMAT_EXCEL',
      });
    },
    ADD_ADVERTISEMENT_PACKAGE: async (context) => {
      const recordData = context.recordsTableData?.content.find(
        (i) => Number(i.id) === context.selectedRowId
      );
      await this.modalsService.openAdvertisementPackageModal('advertisementPackage', recordData!);
    },
    VIEW_BLOGGER_GROUPS: async (context) => {
      if (context.selectedRowId) {
        await this.modalsService.openBloggerGroupsModal<number>(context.selectedRowId, 'detachBloggerGroups');
      }
    },
    LINE_CHART: async (context) => {
      await this.modalsService.openLineChartModal({
        recordsTableData: [],
        fields: context.fields || [],
        parentUrl: context.resourceParams.path,
        queryParams: context.queryParams
      });
    },
    SETTINGS_TABLE: async (context) => {
      await this.modalsService.openSettingsTableModal({
        parentUrl: context.resourceParams.path
      });
    },
    UPLOAD_EXCEL: async (context) => {
      await this.modalsService.openUploadExcelModal({
        parentUrl: context.resourceParams.path
      });
    },
    PRICE_LEVEL_CONFIG_COPY: async (context) => {
      const articleSelectData = context.fields?.find(i => i.key === 'article')?.metaSelectKeyList?.filter(i => i.display);
      await this.modalsService.openPriceLevelConfigCopyModal({
        copyId: context.selectedRowId || 0,
        articleSelectData: articleSelectData || []
      });
    },
    RRC_LIMIT_COPY: async (context) => {
      const articleSelectData = context.fields?.find(i => i.key === 'article')?.metaSelectKeyList?.filter(i => i.display);
      await this.modalsService.openRrcLimitsCopyModal({
        copyId: context.selectedRowId || 0,
        articleSelectData: articleSelectData || []
      });
    }
  }

  public async handleActionSelect(action: RecordTableAction, context: ActionHandlerContext): Promise<void> {
    const handler = this.actionHandlers[action];

    if (handler) {
      await handler(context);
    }
  }

  public handleDownloadExcel(handleDownloadExcelParams: HandleDownloadExcelParams): void {
    const downloadExcelRequestParams = this.setDownloadExcelParams(handleDownloadExcelParams);
    const { resourceParams } = handleDownloadExcelParams;
    this.downloadExcel(downloadExcelRequestParams, resourceParams.name);
  }

  private setDownloadExcelParams({ excelType, resourceParams, queryParams }: HandleDownloadExcelParams): DownloadExcelRequestParams {
    return {
      excelType: excelType,
      path: resourceParams.path,
      queryParams: queryParams
    };
  }

  private downloadExcel(downloadExcelRequestParams: DownloadExcelRequestParams, resourceParamsName: string) {
    this.recordTableApiService.downloadExcel(downloadExcelRequestParams).subscribe({
      next: (data: BlobPart) => this.utilityService.downloadExcelFile(data, resourceParamsName),
      error: (error) => this.errorService.handleError(error)
    });
  }
}
